import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { TickerSnapshotRequest, TickerSnapshotResponse } from '../types/ticker';
import { GetLatestAnalystViewsRequest, GetLatestAnalystViewsResponse, GetPast4QuartersAnalystViewsRequest, GetPast4QuartersAnalystViewsResponse } from '../types/analystViews';
import { getSessionToken } from './sessionTokenService';
import { isTokenExpired, formatTimeUntilExpiration } from '../utils/jwtUtils';
import { refreshAccessToken, shouldRefreshToken } from './tokenRefreshService';

// Create axios instance with default config for authenticated users
const userApi = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/v1`,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication, session token, and logging
userApi.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('accessToken');
    
    // Check if token is expired or should be refreshed before making the request
    if (token) {
      if (isTokenExpired(token)) {
        console.warn('⚠️ userTickerApi: Token is expired, attempting to refresh...');
        const newTokens = await refreshAccessToken();
        if (newTokens) {
          token = newTokens.accessToken;
          console.log('✅ userTickerApi: Token refreshed before request');
        } else {
          console.error('❌ userTickerApi: Failed to refresh expired token');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
          return Promise.reject(new Error('Token expired and refresh failed'));
        }
      } else if (shouldRefreshToken(token)) {
        // Token is expiring soon, refresh in background (non-blocking)
        refreshAccessToken().catch((error) => {
          console.warn('⚠️ userTickerApi: Background token refresh failed:', error);
        });
      }
      
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add session token to all requests (even authenticated ones)
    const sessionToken = getSessionToken();
    if (sessionToken) {
      config.headers['X-Session-Token'] = sessionToken;
    }
    console.log(`Making authenticated API request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Authenticated API request error:', error);
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing to avoid multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Add response interceptor for error handling with automatic token refresh
userApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized errors (expired or invalid token)
    if (error.response?.status === 401 && !originalRequest._retry) {
      const token = localStorage.getItem('accessToken');
      console.warn('⚠️ userTickerApi: Received 401 Unauthorized response');
      if (token) {
        console.log('🔍 userTickerApi: Token expiration info:', formatTimeUntilExpiration(token));
      }

      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return userApi(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newTokens = await refreshAccessToken();
        if (newTokens) {
          console.log('✅ userTickerApi: Token refreshed successfully, retrying request');
          processQueue(null, newTokens.accessToken);
          
          // Update the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          }
          
          isRefreshing = false;
          return userApi(originalRequest);
        } else {
          throw new Error('Failed to refresh token');
        }
      } catch (refreshError) {
        console.error('❌ userTickerApi: Failed to refresh token:', refreshError);
        processQueue(error as AxiosError);
        isRefreshing = false;
        
        // Clear authentication data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        
        // Dispatch custom event to notify AuthContext
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        
        return Promise.reject(refreshError);
      }
    } else if (error.response?.status === 403) {
      console.warn('⚠️ userTickerApi: Received 403 Forbidden response');
      // For 403, we might want to clear auth as well, but be more careful
      // Only clear if it's likely an auth issue, not a permission issue
      const token = localStorage.getItem('accessToken');
      if (token && isTokenExpired(token)) {
        console.log('🔍 userTickerApi: Token is expired, attempting refresh...');
        try {
          const newTokens = await refreshAccessToken();
          if (newTokens && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
            return userApi(originalRequest);
          }
        } catch (refreshError) {
          console.error('❌ userTickerApi: Failed to refresh token on 403:', refreshError);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
      }
    }
    
    console.error('Authenticated API response error:', error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);

export const userTickerApi = {
  /**
   * Get ticker snapshot data for multiple tickers (authenticated)
   */
  async getTickerSnapshot(request: TickerSnapshotRequest): Promise<TickerSnapshotResponse> {
    try {
      const response = await userApi.post<TickerSnapshotResponse>('/user-ticker/snapshot', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get technical data for multiple tickers (authenticated)
   */
  async getTickerTechnical(request: { tickerBasicsIds: number[]; daysAgo?: number; daysAgoArray?: number[]; date?: string; category?: string; indicatorKey?: string; ownerHasProSubscription?: boolean }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/technical', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest performance returns for multiple tickers (authenticated)
   */
  async getTickerPerformance(request: { tickerBasicsIds?: number[]; tickerSymbols?: string[] }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/performance', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get performance history (latest N records) for a ticker (authenticated)
   */
  async getTickerPerformanceHistory(request: { tickerBasicsId: number; limit?: number }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/performance/history', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Check ticker sync status (authenticated)
   */
  async checkTickerSyncStatus(request: { ticker_symbols?: string[]; ticker_basics_ids?: number[] }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/check-sync-status', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest prices for tickers (authenticated)
   */
  async getTickerLatestPrice(request: { ticker_symbols?: string[]; ticker_basics_ids?: number[] }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/latest-price', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get moving average cross data (authenticated)
   */
  async getMovingAverageCrossData(request: { 
    ticker_symbols?: string[]; 
    ticker_basics_ids?: number[]; 
    start_days_ago: number; 
    end_days_ago: number; 
  }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/moving-average-cross', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest analyst views (authenticated)
   */
  async getLatestAnalystViews(request: GetLatestAnalystViewsRequest): Promise<GetLatestAnalystViewsResponse> {
    try {
      const response = await userApi.post<GetLatestAnalystViewsResponse>('/user-ticker/latest-analyst-views', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get past 4 quarters analyst views (authenticated)
   */
  async getPast4QuartersAnalystViews(request: GetPast4QuartersAnalystViewsRequest): Promise<GetPast4QuartersAnalystViewsResponse> {
    try {
      const response = await userApi.post<GetPast4QuartersAnalystViewsResponse>('/user-ticker/past-4-quarters-analyst-views', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get ticker basics (authenticated)
   */
  async getTickerBasics(tickerSymbols: string[]): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/basics', { tickerSymbols: tickerSymbols });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get current quarterly ratios (authenticated)
   */
  async getCurrentQuarterlyRatios(tickerBasicsIds: number[]): Promise<any> {
    try {
      const response = await userApi.get(`/user-ticker/ratios/quarterly/current?ids=${tickerBasicsIds.join(',')}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest ended quarterly ratios with comparisons (authenticated)
   */
  async getLatestEndedQuarterlyRatiosWithComparisons(tickerBasicsIds: number[], ownerHasProSubscription?: boolean): Promise<any> {
    try {
      let url = `/user-ticker/ratios/quarterly/latest-ended-and-comparisons?ids=${tickerBasicsIds.join(',')}`;
      if (ownerHasProSubscription !== undefined) {
        url += `&ownerHasProSubscription=${ownerHasProSubscription}`;
      }
      const response = await userApi.get(url);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest ended quarterly financials with comparisons (authenticated)
   */
  async getLatestEndedQuarterlyFinancialsWithComparisons(tickerBasicsIds: number[]): Promise<any> {
    try {
      const response = await userApi.get(`/user-ticker/financials/latest-and-comparisons?ids=${tickerBasicsIds.join(',')}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest annual ratios with comparisons (authenticated)
   */
  async getLatestAnnualRatiosWithComparisons(tickerBasicsIds: number[], ownerHasProSubscription?: boolean): Promise<any> {
    try {
      let url = `/user-ticker/ratios/annual/compare?ids=${tickerBasicsIds.join(',')}`;
      if (ownerHasProSubscription !== undefined) {
        url += `&ownerHasProSubscription=${ownerHasProSubscription}`;
      }
      const response = await userApi.get(url);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest annual financials with comparisons (authenticated)
   */
  async getLatestAnnualFinancialsWithComparisons(tickerBasicsIds: number[]): Promise<any> {
    try {
      const response = await userApi.get(`/user-ticker/financials/annual/compare?ids=${tickerBasicsIds.join(',')}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get batch daily price data for multiple tickers (authenticated)
   */
  async getBatchTickerDailyPrices(tickerSymbols: string[], recordsPerTicker: number = 2): Promise<{ data: any; meta: any }> {
    try {
      const requestBody = {
        ticker_symbols: tickerSymbols.map(symbol => symbol.toUpperCase()),
        records_per_ticker: recordsPerTicker
      };
      
      const response = await userApi.post('/user-ticker/batch-daily-prices', requestBody);
      
      return { 
        data: response.data.data,
        meta: response.data.meta
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('No tickers found for the provided symbols');
        }
        if (error.response?.status === 500) {
          throw new Error('Server error: Please try again later');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get daily price data for a specific ticker with pagination (authenticated)
   */
  async getTickerDailyPrices(tickerSymbol: string, page: number = 0, limit: number = 50, timeframe?: string, sortColumn?: string, sortDirection?: string): Promise<{ data: any; meta: any }> {
    try {
      const requestBody: any = {
        ticker_symbol: tickerSymbol.toUpperCase(),
        page: page,
        limit: limit
      };
      
      if (timeframe) {
        requestBody.timeframe = timeframe;
      }
      
      if (sortColumn) {
        requestBody.sort_column = sortColumn;
      }
      
      if (sortDirection) {
        requestBody.sort_direction = sortDirection;
      }
      
      const response = await userApi.post('/user-ticker/daily-prices', requestBody);
      
      return { 
        data: response.data.data,
        meta: response.data.meta
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Ticker not found');
        }
        if (error.response?.status === 500) {
          throw new Error('Server error: Please try again later');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Check insider sync status for tickers (authenticated)
   */
  async checkInsiderSyncStatus(request: { ticker_basics_ids: number[] }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/check-insider-sync-status', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get insider transactions by date range (authenticated)
   */
  async getInsiderTransactionsByDateRange(request: { tickerBasicsIds: number[]; startDate: string; endDate: string; ownerHasProSubscription?: boolean }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/insider-transactions/by-date-range', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Check pros/cons sync status for tickers (authenticated)
   */
  async checkProsAndConsSyncStatus(request: { ticker_basics_ids: number[] }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/check-pros-cons-sync-status', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest pros/cons for multiple tickers (authenticated)
   */
  async getLastProsAndCons(request: { tickerBasicsIds: number[]; ownerHasProSubscription?: boolean }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/pros-cons/last', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /** Backwards-compatible alias. */
  async getMonthlyProsAndCons(request: { tickerBasicsIds: number[]; ownerHasProSubscription?: boolean }): Promise<any> {
    return this.getLastProsAndCons(request);
  },

  async getProsAndConsHistory(request: { tickerBasicsId: number; limit?: number; ownerHasProSubscription?: boolean }): Promise<any> {
    try {
      const response = await userApi.post('/user-ticker/pros-cons/history', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid or expired token');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },
};
